import type { ComponentPropsWithoutRef } from 'react';

import type {
  UnitTokenClasses,
  UnitSettings,
  As,
} from '../ModularTypes/UnitSettings';
import { unitTokenKeys } from '../ModularTypes/UnitTokenKey';

type ClassValue = string | undefined | null;

type UnitElementProps<T extends As> = Omit<
  ComponentPropsWithoutRef<T>,
  keyof UnitTokenClasses | 'as' | 'children'
>;

interface UnitBuildResult<T extends As> {
  className: string;
  elementProps: UnitElementProps<T>;
}

export class UnitClassBuilder {
  static build<T extends As>(
    props: UnitSettings<T>,
    context: Partial<UnitTokenClasses> = {},
  ): UnitBuildResult<T> {
    const className = this.buildClassName(props, context);
    const elementProps = this.extractElementProps(props);

    return {
      className,
      elementProps,
    };
  }

  private static buildClassName(
    props: UnitSettings<As>,
    context: Partial<UnitTokenClasses> = {},
  ): string {
    const get = <T extends keyof UnitTokenClasses>(
      key: T,
    ): string | undefined => props[key] || context[key];

    const toClass = (prefix: string, value?: string) =>
      value ? `${prefix}-${value}` : null;

    return [
      'unit',
      props.className ?? context.className,
      get('layout'),

      toClass('bg', get('background')),

      toClass('flex-direction', get('flexDirection')),
      get('flexWrap'),

      toClass('flex-grow', get('flexGrow')),
      toClass('flex-shrink', get('flexShrink')),

      toClass('flex-basis', get('flexBasis')),

      toClass('grid-area', get('gridArea')),

      get('overLay'),
      toClass('col-span', get('colSpan')),
      toClass('row-span', get('rowSpan')),

      toClass('gap', get('gap')),
      toClass('width', get('width')),
      toClass('width-min', get('widthMin')),
      toClass('width-max', get('widthMax')),
      toClass('height', get('height')),
      toClass('height-min', get('heightMin')),
      toClass('height-max', get('heightMax')),

      toClass('justify-content', get('justifyContent')),
      toClass('justify-items', get('justifyItems')),
      toClass('align-content', get('alignContent')),
      toClass('align-items', get('alignItems')),
      toClass('align-self', get('alignSelf')),
      toClass('justify-self', get('justifySelf')),
      toClass('padding-left', get('paddingLeft')),
      toClass('padding-right', get('paddingRight')),
      toClass('padding-top', get('paddingTop')),
      toClass('padding-bottom', get('paddingBottom')),
      toClass('margin', get('margin')),
      toClass('margin-left', get('marginLeft')),
      toClass('margin-right', get('marginRight')),
      toClass('margin-top', get('marginTop')),
      toClass('margin-bottom', get('marginBottom')),

      toClass('box-shadow', get('boxShadow')),
      toClass('z-index', get('zIndex')),

      toClass('border-color', get('borderColor')),
      toClass('border-dark', get('borderDark')),
      toClass('border-light', get('borderLight')),
      toClass('border-radius', get('borderRadius')),
    ]
      .filter(this.isValid)
      .join(' ');
  }

  private static extractElementProps<T extends As>(
    props: UnitSettings<T>,
  ): UnitElementProps<T> {
    const elementProps: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(props)) {
      if (
        key === 'as' ||
        key === 'children' ||
        unitTokenKeys.has(key as keyof UnitTokenClasses)
      ) {
        continue;
      }

      elementProps[key] = value;
    }

    return elementProps as UnitElementProps<T>;
  }

  static isValid(value: ClassValue): value is string {
    return typeof value === 'string' && value.trim() !== '';
  }
}
